import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { fetchRoutes } from '../../store/slices/routesSlice';
import { FiPlus, FiSearch, FiCalendar } from 'react-icons/fi';
import RouteCard from '../../components/Routes/RouteCard';

const RouteList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { routes, loading, error } = useAppSelector((state) => state.routes);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Отладочная информация
  console.log('RouteList render:', { routes, loading, error });

  useEffect(() => {
    console.log('RouteList useEffect - fetching routes...');
    const archived = searchParams.get('archived') === 'true';
    setShowArchived(archived);
    
    const params: Record<string, any> = {};
    if (archived) {
      params.is_archived = true;
    }
    if (searchTerm) {
      params.search = searchTerm;
    }
    
    console.log('RouteList: dispatching fetchRoutes with params:', params);
    dispatch(fetchRoutes(params));
  }, [dispatch, searchParams, searchTerm]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params: Record<string, any> = {};
    if (showArchived) {
      params.archived = 'true';
    }
    if (searchTerm) {
      params.search = searchTerm;
    }
    setSearchParams(params);
  };

  const handleFilterChange = (archived: boolean) => {
    setShowArchived(archived);
    const params: Record<string, any> = {};
    if (archived) {
      params.archived = 'true';
    }
    if (searchTerm) {
      params.search = searchTerm;
    }
    setSearchParams(params);
  };

  // Убеждаемся, что routes - это массив
  const routesArray = Array.isArray(routes) ? routes : [];
  
  const filteredRoutes = routesArray.filter(route => {
    if (searchTerm) {
      return route.title.toLowerCase().includes(searchTerm.toLowerCase());
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-gray-500">Загрузка маршрутов...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Отладочная информация */}
      <div className="bg-yellow-100 p-4 text-yellow-800 rounded-lg">
        RouteList component is rendering! ✅
        <br />
        Routes type: {typeof routes} {Array.isArray(routes) ? '(Array)' : '(Object)'}
        <br />
        Routes count: {Array.isArray(routes) ? routes.length : 'N/A'}
        <br />
        Routes data: {JSON.stringify(routes).substring(0, 100)}...
        <br />
        Loading: {loading ? 'Yes' : 'No'}
        <br />
        Error: {error || 'None'}
      </div>
      
      {/* Заголовок */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {showArchived ? 'Архив маршрутов' : 'Мои маршруты'}
          </h1>
          <p className="text-gray-600 mt-1">
            {filteredRoutes.length} маршрут{filteredRoutes.length !== 1 ? 'ов' : ''}
          </p>
        </div>
        <Link
          to="/routes/create"
          className="btn btn-primary flex items-center space-x-2"
        >
          <FiPlus className="w-4 h-4" />
          <span>Создать маршрут</span>
        </Link>
      </div>

      {/* Фильтры и поиск */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Поиск */}
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Поиск по названию маршрута..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>
        </form>

        {/* Фильтры */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleFilterChange(false)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !showArchived
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Активные
          </button>
          <button
            onClick={() => handleFilterChange(true)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showArchived
                ? 'bg-primary-100 text-primary-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Архив
          </button>
        </div>
      </div>

      {/* Список маршрутов */}
      {filteredRoutes.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiCalendar className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {showArchived ? 'Архив пуст' : 'Нет маршрутов'}
          </h3>
          <p className="text-gray-600 mb-6">
            {showArchived 
              ? 'В архиве пока нет маршрутов'
              : 'Создайте свой первый маршрут путешествия'
            }
          </p>
          {!showArchived && (
            <Link to="/routes/create" className="btn btn-primary">
              Создать маршрут
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRoutes.map((route) => (
            <RouteCard key={route.id} route={route} />
          ))}
        </div>
      )}
    </div>
  );
};

export default RouteList; 